import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    ServiceRequestFormData,
    OrganizationData,
    Discount
} from '../../types';
import { HaulingFeePreview } from '../HaulingFeePreview';
import { formatServiceStatus } from '../../config/serviceStatuses';
import { canAccessFeature } from '../../utils/tierPermissions';
import { computeTransportFee } from '../../utils/haulingFee';
import './StepStyles.scss'; // Shared styles

interface Step7ReviewProps {
  formData: ServiceRequestFormData;
  updateFormData: (data: Partial<ServiceRequestFormData>) => void;
  registerValidation: (validator: () => boolean) => void;
  organizationData: OrganizationData;
}

// Helper to find name by ID
const findNameById = <T extends { id: number; name: string }>(id: number | null | undefined, items: T[]): string => {
    if (id === null || id === undefined) return 'N/A';
    const item = items.find(i => i.id === id);
    return item ? item.name : `ID ${id} (Not Found)`;
};

// Helper to format status
const formatStatus = formatServiceStatus;

// Helper to format currency
const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

function Step7Review({
  formData,
  updateFormData,
  registerValidation,
  organizationData,
}: Step7ReviewProps) {
  const [validationError, setValidationError] = useState<string | null>(null);

  const isFreeFlow = !canAccessFeature(organizationData?.organization?.tier, 'packageSelection');

  // Extract necessary data
  const {
    selectedLocationId, selectedCategoryId, selectedDisplayCategoryId, selectedMakeId, selectedMachineId,
    selectedStatus, requestLocation, shopServicePreference, selectedPackageId,
    distanceToSelectedLocation, haulingFee: previouslyCalculatedHaulingFee
  } = formData;
  const {
    locations = [], allCategories = [], allMakes = [], allMachines = [], packages = [],
    discounts = [], packageDiscounts = [],
    displayCategories = [],
    haulingSettings = null,
    haulingCategoryRates = [],
    haulingDiscounts = []
  } = organizationData;

  // Transport fee the request will carry: hauling (shop pickup) or the flat
  // travel fee (home service) — the same split as the DB trigger.
  const transportFee = useMemo(() => computeTransportFee({
    distance: distanceToSelectedLocation, categoryId: selectedCategoryId, requestLocation, shopServicePreference,
    settings: haulingSettings, categoryRates: haulingCategoryRates, discounts: haulingDiscounts,
  }), [
    distanceToSelectedLocation, selectedCategoryId, requestLocation,
    shopServicePreference, haulingSettings, haulingCategoryRates, haulingDiscounts
  ]);
  const calculatedHaulingFee = transportFee?.fee ?? null;
  const transportLabel = transportFee?.kind === 'travel' ? 'Travel Fee' : 'Hauling Fee';

  // Update form data when calculated fee changes
  useEffect(() => {
    if (calculatedHaulingFee !== previouslyCalculatedHaulingFee) {
      updateFormData({ haulingFee: calculatedHaulingFee });
    }
  }, [calculatedHaulingFee, previouslyCalculatedHaulingFee, updateFormData]);

  // Calculate Package Price & Discount
  const selectedPackage = useMemo(() => {
      if (!selectedPackageId) return null;
      return packages.find(p => p.id === selectedPackageId) ?? null;
  }, [selectedPackageId, packages]);

  const packageDiscountMap = useMemo(() => {
      const map = new Map<number, Discount[]>();
      if (!discounts || !packageDiscounts) return map;
      packageDiscounts.forEach(link => {
          const discount = discounts.find(d => d.id === link.discount_id);
          if (discount && link.package_id) {
              if (!map.has(link.package_id)) map.set(link.package_id, []);
              map.get(link.package_id)?.push(discount);
          }
      });
      return map;
  }, [discounts, packageDiscounts]);

  const getBestDiscount = useCallback((packagePrice: number | null, applicableDiscounts: Discount[] | undefined): { discountedPrice: number; appliedDiscount: Discount; } | null => {
      if (!applicableDiscounts || applicableDiscounts.length === 0 || packagePrice === null) return null;
      const price = Number(packagePrice);
      const calculations = applicableDiscounts.map(discount => {
          let discounted = price;
          if (discount.discount_type === 'percentage') discounted = price * (1 - discount.amount / 100);
          else discounted = Math.max(0, price - discount.amount);
          return { discountedPrice: parseFloat(discounted.toFixed(2)), appliedDiscount: discount };
      });
      return calculations.reduce((best, current) => current.discountedPrice < best.discountedPrice ? current : best);
  }, []);

  const discountInfo = useMemo(() => {
      if (!selectedPackage) return null;
      const applicableDiscounts = packageDiscountMap.get(selectedPackage.id);
      return getBestDiscount(selectedPackage.price, applicableDiscounts);
  }, [selectedPackage, packageDiscountMap, getBestDiscount]);

  const packagePrice = selectedPackage?.price ?? 0;
  const finalPackagePrice = discountInfo?.discountedPrice ?? packagePrice;

  // Calculate Estimated Total
  const estimatedTotal = useMemo(() => {
      const fee = calculatedHaulingFee ?? 0; // Use calculatedHaulingFee directly
      const total = finalPackagePrice + fee;
      return parseFloat(total.toFixed(2));
  }, [finalPackagePrice, calculatedHaulingFee]);

  // Display Names
  const selectedLocationName = useMemo(() => findNameById(selectedLocationId, locations), [selectedLocationId, locations]);
  const selectedCategoryName = useMemo(() => {
    if (selectedDisplayCategoryId) {
      const dc = displayCategories.find(d => d.id === selectedDisplayCategoryId);
      if (dc) return dc.name;
    }
    return findNameById(selectedCategoryId, allCategories);
  }, [selectedDisplayCategoryId, displayCategories, selectedCategoryId, allCategories]);
  const selectedMakeName = useMemo(() => findNameById(selectedMakeId, allMakes), [selectedMakeId, allMakes]);
  const selectedMachineName = formData.selectedMachineName || findNameById(selectedMachineId, allMachines);
  const selectedPackageName = selectedPackage?.name ?? 'N/A';
  const selectedStatusLabel = useMemo(() => formatStatus(selectedStatus), [selectedStatus]);

  // Event Handler for Confirmation
  const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFormData({ reviewConfirmed: e.target.checked });
    if (e.target.checked) {
      setValidationError(null);
    }
  };

  // Handler for Notes
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateFormData({ serviceNotes: e.target.value });
  };

  // Validation
  const validateStep = useCallback(() => {
    if (!formData.reviewConfirmed) {
      setValidationError("Please confirm the details below before submitting.");
      return false;
    }
    setValidationError(null);
    return true;
  }, [formData.reviewConfirmed]);

  // Register validation
  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  // Render
  return (
    <div className="form-step step-review">
      <h2>Review & Submit Your Request</h2>
      <p className="step-intro">
        Please review all details and the estimated cost below. Click Submit when ready.
      </p>

      {validationError && <div className="error-message summary-error">{validationError}</div>}

      {/* Your Information Section */}
      <div className="review-section">
        <h4>Your Information</h4>
        <div className="review-grid">
          <div className="review-item"><label>Name:</label> <span>{formData.firstName || 'N/A'} {formData.lastName || 'N/A'}</span></div>
          <div className="review-item"><label>Email:</label> <span>{formData.email || 'N/A'}</span></div>
          <div className="review-item"><label>Mobile Phone:</label> <span>{formData.mobilePhone || 'N/A'}</span></div>
          <div className="review-item"><label>Home Phone:</label> <span>{formData.homePhone || 'N/A'}</span></div>
          <div className="review-item full-width"><label>Address:</label> <span>{`${formData.address || ''}, ${formData.city || ''}, ${formData.state || ''} ${formData.zipCode || ''}`.replace(/ ,/g, '').replace(/^,/, '').trim() || 'N/A'}</span></div>
          <div className="review-item"><label>Preferred Contact:</label> <span>{formData.preferredContact || 'N/A'}</span></div>
          <div className="review-item"><label>SMS Consent:</label> <span>{formData.smsConsent ? 'Yes' : 'No'}</span></div>
        </div>
      </div>

      <hr className="review-divider"/>

      {/* Service Location Section */}
      <div className="review-section">
        <h4>Service Location</h4>
        <div className="review-grid single-item">
          <div className="review-item"><label>Selected Location:</label> <span>{selectedLocationName}</span></div>
          {!isFreeFlow && formData.distanceToSelectedLocation !== null && formData.distanceToSelectedLocation !== undefined && (
            <div className="review-item"><label>Approx. Distance:</label> <span>{`${formData.distanceToSelectedLocation.toFixed(1)} miles`}</span></div>
          )}
        </div>
      </div>

      <hr className="review-divider"/>

      {/* Machine Details Section */}
      <div className="review-section">
        <h4>Machine Details</h4>
        <div className="review-grid">
          <div className="review-item"><label>Category:</label> <span>{selectedCategoryName}</span></div>
          <div className="review-item"><label>Make:</label> <span>{selectedMakeName}</span></div>
          <div className="review-item"><label>Machine/Model:</label> <span>{selectedMachineName}</span></div>
        </div>
      </div>

      <hr className="review-divider"/>

      {/* Conditional Section: Free Tier vs Basic+ Tier */}
      {isFreeFlow ? (
        /* Free Tier: Show Request Description */
        <div className="review-section">
          <h4>Service Request</h4>
          <div className="review-grid">
            <div className="review-item full-width">
              <label>Request Description:</label>
              <p className="request-description-text">{formData.requestDescription || 'N/A'}</p>
            </div>
          </div>
        </div>
      ) : (
        /* Basic+ Tier: Show Reason for Service + Pricing */
        <>
          <div className="review-section">
            <h4>Reason for Service</h4>
            <div className="review-grid">
              <div className="review-item"><label>Status:</label> <span>{selectedStatusLabel}</span></div>
              <div className="review-item"><label>Service Location:</label> <span>{formData.requestLocation === 'home' ? 'At My Location' : formData.requestLocation === 'shop' ? 'At Shop' : 'N/A'}</span></div>
              {formData.requestLocation === 'shop' && (
                <div className="review-item"><label>Transport Method:</label> <span>{formData.shopServicePreference === 'drop_off' ? 'Customer Drop-off' : 'Shop Pickup'}</span></div>
              )}
            </div>
          </div>

          {/* Pricing Summary Section */}
          <hr className="review-divider"/>
          <div className="review-section pricing-summary">
            <h4>Estimated Cost</h4>

            {/* Show package price if selected */}
            {selectedPackage && (
              <>
                <div className="price-line">
                  <span className="price-label">{selectedPackageName} Price:</span>
                  <span className="price-value">{formatCurrency(packagePrice)}</span>
                </div>
                {discountInfo && (
                  <div className="price-line discount-line">
                    <span className="price-label">Discount ({discountInfo.appliedDiscount.name}):</span>
                    <span className="price-value negative">
                      - {discountInfo.appliedDiscount.discount_type === 'percentage'
                        ? `${discountInfo.appliedDiscount.amount}%`
                        : formatCurrency(discountInfo.appliedDiscount.amount)}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Show hauling fee if applicable */}
            {(calculatedHaulingFee !== null && calculatedHaulingFee > 0) && (
              <div className="price-line hauling-line">
                <span className="price-label">{transportLabel}:</span>
                <span className="price-value">{formatCurrency(calculatedHaulingFee)}</span>
              </div>
            )}

            {/* Show repair quote pending or estimated total */}
            {formData.diagnosticQuoteId ? (
              <>
                {(selectedPackage || (calculatedHaulingFee !== null && calculatedHaulingFee > 0)) && (
                  <>
                    <hr className="price-divider"/>
                    <div className="price-line">
                      <span className="price-label">Subtotal (Known Costs):</span>
                      <span className="price-value">{formatCurrency(estimatedTotal)}</span>
                    </div>
                  </>
                )}
                <div className="price-line">
                  <span className="price-label">Repair Quote:</span>
                  <span className="price-value">Pending</span>
                </div>
                <p className="disclaimer">
                  We are currently preparing a quote for your repair and will contact you with pricing shortly.
                  {(selectedPackage || (calculatedHaulingFee !== null && calculatedHaulingFee > 0)) && (
                    <> The estimate above reflects {selectedPackage && (calculatedHaulingFee !== null && calculatedHaulingFee > 0) ? 'package and transport' : selectedPackage ? 'package' : 'transport'} costs; repair costs will be provided separately.</>
                  )}
                </p>
              </>
            ) : selectedPackage ? (
              <>
                <hr className="price-divider"/>
                <div className="price-line total-line">
                  <span className="price-label">Estimated Total:</span>
                  <span className="price-value">{formatCurrency(estimatedTotal)}</span>
                </div>
                <p className="disclaimer">Note: This is an estimate. Actual charges may vary based on final diagnosis, parts, and labor.</p>
              </>
            ) : null}
          </div>

          {/* Detailed transport fee breakdown (hauling or travel) */}
          {calculatedHaulingFee !== null && calculatedHaulingFee > 0 && distanceToSelectedLocation && selectedCategoryId && haulingSettings && (
            <div className="review-section">
              <HaulingFeePreview
                distance={distanceToSelectedLocation!}
                categoryId={selectedCategoryId!}
                requestLocation={requestLocation}
                settings={haulingSettings!}
                categoryRates={haulingCategoryRates}
                discounts={haulingDiscounts}
              />
            </div>
          )}
        </>
      )}

      {/* Service Notes Section */}
      <hr className="review-divider"/>
      <div className="form-section">
        <h4>Service Notes (Optional)</h4>
        <div className="form-group full-width">
          <label htmlFor="serviceNotesReview">Add any specific details or requests:</label>
          <textarea
            id="serviceNotesReview"
            name="serviceNotes"
            rows={3}
            value={formData.serviceNotes || ''}
            onChange={handleNotesChange}
            placeholder="e.g., Machine making strange noise, specific part needed..."
          />
        </div>
      </div>

      {/* Confirmation Checkbox */}
      <hr className="review-divider"/>
      <div className="form-group confirmation-section">
        <div className="checkbox-option">
          <input 
            type="checkbox" 
            id="reviewConfirmed" 
            name="reviewConfirmed" 
            checked={formData.reviewConfirmed || false} 
            onChange={handleConfirmationChange} 
            required 
          />
          <label htmlFor="reviewConfirmed">
            I have reviewed the information and estimate, and confirm it is accurate.
          </label>
        </div>
      </div>
    </div>
  );
}

export default Step7Review;